import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Typography, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
    ShopOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { usePlan } from '@/hooks/usePlan';
import { buildSidebarMenuItems } from './menuItems';

type SidebarItem = NonNullable<MenuProps['items']>[number];

function normalizeMenuKey(key: string): string {
    const withoutScopedSuffix = key.split('__')[0];
    return withoutScopedSuffix.endsWith('-root')
        ? withoutScopedSuffix.slice(0, -'-root'.length)
        : withoutScopedSuffix;
}

interface MenuKeyEntry {
    rawKey: string;
    normalizedPath: string;
}

function collectMenuKeys(items: NonNullable<MenuProps['items']>): MenuKeyEntry[] {
    const keys: MenuKeyEntry[] = [];
    for (const item of items) {
        if (!item || (item as { type?: string }).type === 'divider') continue;
        if ('key' in item && item.key) {
            const rawKey = String(item.key);
            keys.push({ rawKey, normalizedPath: normalizeMenuKey(rawKey) });
        }
        const children = (item as SidebarItem & { children?: NonNullable<MenuProps['items']> }).children;
        if (Array.isArray(children)) {
            keys.push(...collectMenuKeys(children));
        }
    }
    return keys;
}

function findBestSelectedKey(pathname: string, items: NonNullable<MenuProps['items']>): string {
    const keys = collectMenuKeys(items);
    let best = '/';
    let bestLen = 1;
    let exactMatch: string | null = null;
    for (const entry of keys) {
        if (entry.normalizedPath === pathname) {
            if (!exactMatch) {
                exactMatch = entry.rawKey;
            } else {
                const currentIsScoped = entry.rawKey.includes('__');
                const bestIsScoped = exactMatch.includes('__');
                if (currentIsScoped && !bestIsScoped) {
                    exactMatch = entry.rawKey;
                }
            }
            continue;
        }
        if (
            entry.normalizedPath !== '/' &&
            pathname.startsWith(entry.normalizedPath + '/') &&
            entry.normalizedPath.length > bestLen
        ) {
            best = entry.rawKey;
            bestLen = entry.normalizedPath.length;
        }
    }
    if (exactMatch) return exactMatch;
    return best;
}

function itemContainsKey(item: SidebarItem, leafKey: string): boolean {
    if (!item || (item as { type?: string }).type === 'divider') return false;
    if ('key' in item && item.key && String(item.key) === leafKey) return true;
    const children = (item as SidebarItem & { children?: NonNullable<MenuProps['items']> }).children;
    return Array.isArray(children) && children.some((child) => itemContainsKey(child, leafKey));
}

/**
 * Given a leaf key and the top-level menu items, find the parent group key
 * whose children tree contains that leaf key. Returns null if not found.
 */
function findParentGroupKey(
    leafKey: string,
    items: NonNullable<MenuProps['items']>,
): string | null {
    for (const item of items) {
        if (!item) continue;
        if (!('children' in item) || !Array.isArray(item.children)) continue;
        const hasChild = (item.children as NonNullable<MenuProps['items']>).some((child) =>
            itemContainsKey(child, leafKey),
        );
        if (hasChild && item.key) {
            return String(item.key);
        }
    }
    return null;
}

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
    collapsed?: boolean;
    onCollapse?: (collapsed: boolean) => void;
    isMobile?: boolean;
    onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
    collapsed = false,
    onCollapse,
    isMobile = false,
    onNavigate,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { hasPermission } = useRbac();
    const { tier: planTier } = usePlan();
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    // handleNavigate must be declared BEFORE menuItems construction
    const handleNavigate = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    // Build menuItems BEFORE useEffect so it can reference menuItems
    const menuItems = buildSidebarMenuItems({
        hasPermission,
        onNavigate: handleNavigate,
        userRole: user?.role,
        planTier,
    });

    // leafKey + parentGroupKey must be declared BEFORE useEffect that depends on them
    const leafKey = useMemo(
        () => findBestSelectedKey(location.pathname, menuItems),
        [location.pathname, menuItems],
    );
    const selectedKeys = useMemo(() => {
        const parentKey = findParentGroupKey(leafKey, menuItems);
        return parentKey ? [leafKey, parentKey] : [leafKey];
    }, [leafKey, menuItems]);

    // On mobile: when user re-opens the drawer after navigation,
    // restore openKeys so the submenu that contains the current page is expanded.
    // This fixes the bug where the menu closes on item click but doesn't re-highlight
    // the parent group on the next open — user loses orientation.
    // Only sync openKeys when the drawer is open (!collapsed).
    // On mobile, collapsed=true means the drawer is closed (width=0).
    useEffect(() => {
        if (!isMobile || !leafKey || collapsed) return;
        const parentGroupKey = findParentGroupKey(leafKey, menuItems);
        if (parentGroupKey) {
            setOpenKeys((prev) => (prev.includes(parentGroupKey) ? prev : [parentGroupKey]));
        }
    }, [location.pathname, isMobile, leafKey, menuItems]);

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            onCollapse={onCollapse}
            width={240}
            collapsedWidth={isMobile ? 0 : 80}
            theme="dark"
            style={{
                background: 'var(--sidebar-bg)',
                boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
                zIndex: isMobile ? 100 : 98,
                position: isMobile ? 'fixed' : 'sticky',
                left: 0,
                top: 0,
                bottom: 0,
                height: '100vh',
            }}
            // AntD Sider wraps content in `.ant-layout-sider-children` — force flex column there
            // so the menu can overflow-auto while logo + user info stay pinned.
            className="erp-sidebar"
        >
            <style>{`
        .erp-sidebar > .ant-layout-sider-children {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }
        /* Hover effect — light bg on items in dark sidebar */
        .erp-sidebar .ant-menu-dark .ant-menu-item:hover,
        .erp-sidebar .ant-menu-dark .ant-menu-submenu-title:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        /* Selected parent group — highlight submenu title when a child is active */
        .erp-sidebar .ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
          background-color: rgba(255, 255, 255, 0.12) !important;
        }
        /* Popup submenu (hover flyout) — match sidebar dark theme */
        .ant-menu-submenu-popup.ant-menu-dark > .ant-menu {
          background: var(--sidebar-bg);
          border-radius: 8px;
          box-shadow: 4px 0 16px rgba(0, 0, 0, 0.25);
          min-width: 200px;
          padding: 4px 0;
        }
        .ant-menu-submenu-popup.ant-menu-dark .ant-menu-item:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .ant-menu-submenu-popup.ant-menu-dark .ant-menu-item-selected {
          background-color: var(--sidebar-active-bg, #a4193d) !important;
        }
        /* Multi-column popup cho group có nhiều children — giảm chiều dài
           popup khi hover. Dùng CSS columns vì antd Menu vertical popup
           render danh sách dạng flat <li>, columns tự chia. */
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu {
          column-count: 2;
          column-gap: 0;
          width: 480px;
          max-height: 80vh;
          overflow-y: auto;
        }
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu > .ant-menu-item,
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu > .ant-menu-item-divider {
          break-inside: avoid;
        }
      /* Kế toán dùng sections cấp 2 để tránh mega menu quá cao. */
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu {
          width: 260px;
          max-height: min(520px, calc(100vh - 24px));
          overflow-y: auto;
          padding: 6px;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu {
          margin: 2px 0;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu > .ant-menu-submenu-title {
          margin: 0;
          width: 100%;
          height: 38px;
          line-height: 38px;
          border-left: 3px solid rgba(255, 255, 255, 0.12);
          border-radius: 6px;
          padding-inline: 12px 28px !important;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu > .ant-menu-submenu-title:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting-section > .ant-menu {
          width: 260px;
          max-height: min(520px, calc(100vh - 24px));
          overflow-y: auto;
          padding: 6px;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting-section .ant-menu-item {
          margin: 2px 0;
          width: 100%;
          border-radius: 6px;
          padding-inline: 12px !important;
        }
        .erp-accounting-section-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .erp-accounting-section-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
          flex-shrink: 0;
        }
        .erp-sidebar .ant-menu-inline .erp-accounting-section-label {
          color: rgba(255, 255, 255, 0.9);
        }
      `}</style>
            {/* Logo */}
            <div
                style={{
                    height: 64,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: collapsed ? '0 8px' : '0 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <ShopOutlined style={{ fontSize: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
                    {!collapsed && (
                        <Text
                            strong
                            style={{
                                fontSize: 15,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            ERP-Gold
                        </Text>
                    )}
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => onCollapse?.(!collapsed)}
                    style={{ color: '#fff', fontSize: 14, padding: 4 }}
                />
            </div>

            {/* Navigation Menu — scrollable region */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <Menu
                    // Expanded desktop → vertical (always popup children on hover, AMIS-style).
                    // Mobile drawer + collapsed rail → inline (antd native inline-expand / icon+popup).
                    //
                    // QUAN TRỌNG: vertical mode phải để Menu uncontrolled (KHÔNG truyền
                    // `openKeys` prop ngay cả khi undefined) — antd treat presence of
                    // openKeys prop là "controlled" → hover popup không mở. Spread
                    // conditional để chỉ truyền openKeys ở inline mode.
                    {...(!isMobile && !collapsed
                        ? { mode: 'vertical' as const }
                        : { mode: 'inline' as const, openKeys, onOpenChange: setOpenKeys })}
                    theme="dark"
                    selectedKeys={selectedKeys}
                    items={menuItems}
                    triggerSubMenuAction="hover"
                    subMenuOpenDelay={0.1}
                    style={{
                        background: 'var(--sidebar-bg)',
                        border: 'none',
                    }}
                />
            </div>
        </Sider>
    );
};
